import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Mic, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoicesPanel() {
  const [open, setOpen] = useState(false);
  const [voices, setVoices] = useState([]);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("female");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/voices/custom`).then((r) => setVoices(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const clone = async () => {
    if (!name.trim() || !file) { toast.error("Give the voice a name and pick an audio file"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("gender", gender);
      form.append("file", file);
      await axios.post(`${API}/voices/clone`, form, { timeout: 120000 });
      toast.success(`Voice "${name.trim()}" cloned — it now appears in every voice picker`);
      setName(""); setFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cloning failed");
    } finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete cloned voice "${v.name}"? Existing rendered videos keep their audio.`)) return;
    try { await axios.delete(`${API}/voices/custom/${v.id}`); load(); } catch (_e) { toast.error("Delete failed"); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 rounded-lg border border-white/10 text-xs uppercase tracking-[0.18em] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center gap-1.5"
        data-testid="voices-panel-open"
      >
        <Mic className="w-3.5 h-3.5" /> Voices
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#14120d] p-5 my-8" onClick={(e) => e.stopPropagation()} data-testid="voices-panel-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg">Cloned voices</h3>
              <button onClick={() => setOpen(false)} data-testid="voices-panel-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[color:var(--muted)] mb-4">
              Clone a narrator's voice from a recording (≥30s of clear speech). Cloned voices speak every language — pick them in any voice dropdown. Only clone voices you have written permission to use.
            </p>

            {voices.length > 0 && (
              <div className="space-y-2 mb-5">
                {voices.map((v) => (
                  <div key={v.id} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2" data-testid={`voice-row-${v.id}`}>
                    <div>
                      <div className="text-sm font-medium">{v.name}</div>
                      <div className="text-[10px] text-[color:var(--muted)] uppercase tracking-widest">{v.gender} · multilingual · ElevenLabs</div>
                    </div>
                    <button onClick={() => remove(v)} className="text-[color:var(--muted)] hover:text-red-400" data-testid={`voice-delete-${v.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-white/10 pt-4">
              <div className="text-xs uppercase tracking-widest text-[color:var(--muted)] mb-2">Clone a new voice</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Voice name (e.g. Maïra)"
                className="w-full mb-2 bg-black/30 border border-white/10 rounded px-3 py-2 text-sm"
                data-testid="voice-name-input"
              />
              <div className="flex gap-2 mb-2">
                {["female", "male"].map((g) => (
                  <button
                    key={g}
                    onClick={() => setGender(g)}
                    className={`text-xs px-3 py-1.5 rounded border ${gender === g ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-white/15"}`}
                    data-testid={`voice-gender-${g}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              <input
                type="file"
                accept="audio/*,video/mp4"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full mb-3 text-xs text-[color:var(--muted)]"
                data-testid="voice-file-input"
              />
              <button
                onClick={clone}
                disabled={busy}
                className="btn-primary text-sm px-4 py-2 w-full disabled:opacity-50"
                data-testid="voice-clone-btn"
              >
                {busy ? "Cloning…" : "Clone voice"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
